import { WindowUnloadService } from './../../../../services/window-unload.service';
import { CommonModule } from '@angular/common';
import {
  Component,
  computed,
  effect,
  HostListener,
  inject,
  OnDestroy,
  signal,
} from '@angular/core';
import { CustomerDeliveryNoteSigningService } from '@digiwall/goliath-documents/proxy';
import { DialogBoxComponent } from '@digiwall/ui-kit';
import { QRCodeComponent } from 'angularx-qrcode';
import { EnvironmentService } from '@digiwall/goliath-core';
import { LocalizationModule } from '@abp/ng.core';
import {
  CustomerDeliveryNoteSigningHubService,
  DocumentSigningStatus,
} from './customer-delivery-note-signing-hub.service';
import { Subject, takeUntil } from 'rxjs';

@Component({
  selector: 'document-customer-delivery-note-signing-dialog',
  templateUrl: './customer-delivery-note-signing-dialog.component.html',
  styleUrls: ['./customer-delivery-note-signing-dialog.component.scss'],
  providers: [CustomerDeliveryNoteSigningHubService],
  imports: [DialogBoxComponent, CommonModule, QRCodeComponent, LocalizationModule],
})
export class CustomerDeliveryNoteSigningComponent implements OnDestroy {
  public readonly signingKey = signal<string>(null);
  public readonly qrSize = signal(this.resolveQrSize());

  private destroy$ = new Subject<void>();

  private readonly service = inject(CustomerDeliveryNoteSigningService);
  private readonly environment = inject(EnvironmentService);
  private readonly hub = inject(CustomerDeliveryNoteSigningHubService);
  private readonly windowUnloadService = inject(WindowUnloadService);

  protected readonly SigningStatus = DocumentSigningStatus;

  protected readonly qrData = computed(() => {
    const signingKey = this.signingKey();
    if (this.isQrDisabled()) {
      return 'INVALID';
    }
    return `${this.environment.getEnvironment().application.baseUrl}/public/delivery-note-signature/?signingKey=${signingKey}`;
  });
  protected readonly signingStatus = signal(DocumentSigningStatus.NotStarted);

  protected readonly isQrDisabled = computed(
    () => this.signingStatus() != DocumentSigningStatus.NotStarted,
  );

  protected readonly overlayIcon = computed(() => {
    switch (this.signingStatus()) {
      case DocumentSigningStatus.InProgress:
        return 'digi-out-clock-2';
      case DocumentSigningStatus.Completed:
        return 'digi-out-check-1';
      case DocumentSigningStatus.Cancelled:
        return 'digi-out-cross-circle';
      default:
        return '';
    }
  });
  protected readonly overlayMessageKey = computed(() => {
    switch (this.signingStatus()) {
      case DocumentSigningStatus.InProgress:
        return 'Doc::CustomerDeliveryNote:SigningInProgress';
      case DocumentSigningStatus.Completed:
        return 'Doc::CustomerDeliveryNote:SigningCompleted';
      case DocumentSigningStatus.Cancelled:
        return 'Doc::CustomerDeliveryNote:SigningCancelled';
      default:
        return null;
    }
  });

  private readonly shouldAbandon = computed(() => {
    const signingKey = this.signingKey();
    switch (this.signingStatus()) {
      case DocumentSigningStatus.NotStarted:
      case DocumentSigningStatus.InProgress:
        return signingKey != null;
      default:
        return false;
    }
  });

  constructor() {
    const startHub = effect(() => {
      if (this.signingKey() == null) return;

      this.hub.startConnection(this.signingKey()).subscribeOnce(() => {
        this.hub
          .requestStateChanged()
          .pipe(takeUntil(this.destroy$))
          .subscribe(state => this.signingStatus.set(state));
      });

      startHub.destroy();
    });
  }

  @HostListener('window:resize')
  public onWindowResize(): void {
    this.qrSize.set(this.resolveQrSize());
  }

  private resolveQrSize(): number {
    const viewportWidth = globalThis?.innerWidth ?? 1024;
    return Math.max(180, Math.min(320, Math.round(viewportWidth * 0.32)));
  }

  public openSigningPage(): void {
    if (this.isQrDisabled()) {
      return;
    }
    globalThis.open(this.qrData(), '_blank', 'noopener,noreferrer');
  }

  public ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
    this.hub.endConnection();

    if (this.shouldAbandon()) {
      this.service.abandonSigningRequest(this.signingKey()).subscribeOnce(() => {});
    }
  }

  @HostListener('window:beforeunload')
  onWindowUnload() {
    const signingKey = this.signingKey();
    if (this.shouldAbandon()) {
      this.windowUnloadService.makeUnloadRequest(
        'POST',
        `/api/goliath-documents/customer-delivery-note-signing/abandon-signing-request?signingKey=${encodeURIComponent(signingKey)}`,
      );
    }
  }
}
